import React, {useEffect, useRef} from 'react';
import {captureException} from '@sentry/gatsby';
import firebase from 'firebase/compat/app';
import {IntercomProvider, useIntercom} from 'react-use-intercom';

import {getIntercomWebIdentity} from '../utils/firebase';
import {
  clearIntercomOpenRequest,
  isIntercomOpenRequested,
  rememberCurrentIntercomAuthReturnTarget,
} from '../utils/accountPostLoginRedirect';
import {useNavigation} from '../context/NavigationContext';

const INTERCOM_APP_ID = 'rgo5g0vn';

type Props = {
  children: React.ReactNode;
};

const isIntercomDisabledPathname = (pathname: string) =>
  pathname === '/wrapped' || pathname.startsWith('/wrapped/');

function IntercomSession({
  disabled,
  location,
}: {
  disabled: boolean;
  location: string;
}) {
  const {boot, show, shutdown, update} = useIntercom();
  const isBootedRef = useRef(false);
  const previousLocationRef = useRef<string | null>(null);

  useEffect(() => {
    let sessionRevision = 0;

    if (disabled) {
      isBootedRef.current = false;
      shutdown();
      return undefined;
    }

    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      sessionRevision += 1;
      const activeRevision = sessionRevision;

      const bootForUser = async () => {
        isBootedRef.current = false;
        shutdown();

        const reopenIfRequested = () => {
          if (!isIntercomOpenRequested()) return;
          show();
          clearIntercomOpenRequest();
        };

        if (!user || !user.email || !user.emailVerified) {
          boot();
          isBootedRef.current = true;
          reopenIfRequested();
          return;
        }

        try {
          const {intercomUserJwt} = await getIntercomWebIdentity();

          if (activeRevision === sessionRevision) {
            boot({intercomUserJwt});
            isBootedRef.current = true;
            reopenIfRequested();
          }
        } catch (error) {
          captureException(error);

          if (activeRevision === sessionRevision) {
            boot();
            isBootedRef.current = true;
            reopenIfRequested();
          }
        }
      };

      bootForUser();
    });

    return () => {
      sessionRevision += 1;
      isBootedRef.current = false;
      unsubscribe();
      shutdown();
    };
  }, [boot, disabled, show, shutdown]);

  useEffect(() => {
    const previousLocation = previousLocationRef.current;
    previousLocationRef.current = location;

    if (
      disabled ||
      !location ||
      !previousLocation ||
      previousLocation === location ||
      !isBootedRef.current
    ) {
      return;
    }

    update();
  }, [disabled, location, update]);

  return null;
}

export default function IntercomMessenger({children}: Props) {
  const {currentLocation} = useNavigation();
  const browserLocation =
    typeof window === 'undefined'
      ? ''
      : `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const location = currentLocation || browserLocation;
  const pathname = location.split(/[?#]/)[0];
  const disabled = isIntercomDisabledPathname(pathname);

  return (
    <IntercomProvider
      appId={INTERCOM_APP_ID}
      autoBoot={false}
      onShow={rememberCurrentIntercomAuthReturnTarget}
    >
      <IntercomSession disabled={disabled} location={location} />
      {children}
    </IntercomProvider>
  );
}
